import React, { useState, useEffect } from 'react';
import api from '../api';

const FORM_INICIAL = {
  recheio_principal_id: '',
  recheio_secundario_id: '',
  camadas_principal: '2',
  camadas_secundario: '1',
  nome: '',
};

export default function GuiaCombinacoes() {
  const [combinacoes, setCombinacoes] = useState([]);
  const [recheios, setRecheios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    setLoading(true);
    try {
      const [resCombinacoes, resRecheios] = await Promise.all([
        api.get('/guia/combinacoes'),
        api.get('/guia/recheios'),
      ]);
      setCombinacoes(resCombinacoes.data);
      setRecheios(resRecheios.data);
    } catch {
      setErro('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }

  // ── Modal ─────────────────────────────────────────────────────────────────────
  function abrirNovo() {
    setForm(FORM_INICIAL);
    setEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(c) {
    setForm({
      recheio_principal_id: c.recheio_principal_id,
      recheio_secundario_id: c.recheio_secundario_id,
      camadas_principal: c.camadas_principal,
      camadas_secundario: c.camadas_secundario,
      nome: c.nome,
    });
    setEditando(c.id);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setEditando(null);
  }

  function handleChange(e) {
    const { name, value } = e.target;

    // Quando muda camadas_principal, ajusta secundario automaticamente (total = 3)
    if (name === 'camadas_principal') {
      const cp = Number(value);
      setForm(prev => ({
        ...prev,
        camadas_principal: cp,
        camadas_secundario: 3 - cp,
      }));
      return;
    }

    setForm(prev => ({ ...prev, [name]: value }));
  }

  // Auto-gera nome ao selecionar os dois recheios
  function handleRecheioChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      const p = recheios.find(r => r.id === Number(updated.recheio_principal_id));
      const s = recheios.find(r => r.id === Number(updated.recheio_secundario_id));
      if (p && s) {
        updated.nome = p.id === s.id ? p.nome : `${p.nome} + ${s.nome}`;
      }
      return updated;
    });
  }

  // ── Salvar ────────────────────────────────────────────────────────────────────
  async function salvar(e) {
    e.preventDefault();
    const cp = Number(form.camadas_principal);
    const cs = Number(form.camadas_secundario);
    if (cp + cs !== 3) {
      setErro('A soma das camadas deve ser 3');
      return;
    }

    setSalvando(true);
    setSucesso('');
    setErro('');

    const payload = {
      nome: form.nome,
      recheio_principal_id: Number(form.recheio_principal_id),
      recheio_secundario_id: Number(form.recheio_secundario_id),
      camadas_principal: cp,
      camadas_secundario: cs,
    };

    try {
      if (editando) {
        await api.put(`/guia/combinacoes/${editando}`, payload);
        setSucesso('Combinação atualizada!');
      } else {
        await api.post('/guia/combinacoes', payload);
        setSucesso('Combinação cadastrada!');
      }
      fecharModal();
      carregarDados();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao salvar combinação');
    } finally {
      setSalvando(false);
    }
  }

  async function desativar(id) {
    if (!window.confirm('Desativar esta combinação?')) return;
    try {
      await api.delete(`/guia/combinacoes/${id}`);
      setSucesso('Combinação desativada.');
      carregarDados();
    } catch {
      setErro('Erro ao desativar combinação');
    }
  }

  // ── Render visual das camadas ─────────────────────────────────────────────────
  function renderCamadas(principal, nPrincipal, secundario, nSecundario) {
    const camadas = [];
    for (let i = 0; i < nPrincipal; i++) camadas.push({ nome: principal, tipo: 'principal' });
    for (let i = 0; i < nSecundario; i++) camadas.push({ nome: secundario, tipo: 'secundario' });
    return (
      <div className="flex gap-1">
        {camadas.map((c, i) => (
          <span
            key={i}
            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
              c.tipo === 'principal'
                ? 'bg-pink-100 text-pink-700'
                : 'bg-purple-100 text-purple-700'
            }`}
          >
            {c.nome}
          </span>
        ))}
      </div>
    );
  }

  // ── Filtros ──────────────────────────────────────────────────────────────────
  const [busca, setBusca] = React.useState('');

  const combinacoesFiltradas = combinacoes.filter(c =>
    !busca || c.nome.toLowerCase().includes(busca.toLowerCase()) ||
    c.recheio_principal_nome?.toLowerCase().includes(busca.toLowerCase()) ||
    c.recheio_secundario_nome?.toLowerCase().includes(busca.toLowerCase())
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-700">Combinações de Recheio</h1>
          <p className="text-gray-400 text-sm mt-1">Distribuição de camadas para bolos com 3 recheios</p>
        </div>
        <button onClick={abrirNovo}
          className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm">
          + Nova Combinação
        </button>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-700 mb-5">
        <strong>Como funciona:</strong> Para bolos com 3 camadas de recheio, você define quantas camadas
        vão de cada recheio. Ex: Brigadeiro + Mousse → 2 camadas Brigadeiro + 1 camada Mousse.
        Para 2 ou 4 camadas a divisão é automática (1+1 ou 2+2).
      </div>

      {erro && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{erro}</div>}
      {sucesso && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">{sucesso}</div>}

      {/* Busca */}
      <div className="mb-6">
        <div className="relative max-w-sm">
          <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
          <input type="text" placeholder="Buscar por nome ou recheio..."
            value={busca} onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-300 focus:border-transparent" />
          {busca && (
            <button onClick={() => setBusca('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-sm">✕</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : combinacoesFiltradas.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Nenhuma combinação encontrada</p>
          <p className="text-gray-400 text-sm mt-1">{busca ? 'Tente ajustar a busca' : 'Clique em "+ Nova Combinação" para começar'}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto mb-4">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Nome</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Distribuição das camadas</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700" style={{width:'90px'}}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {combinacoesFiltradas.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="border border-gray-300 px-3 py-2 font-semibold text-gray-800">{c.nome}</td>
                    <td className="border border-gray-300 px-3 py-2">
                      {renderCamadas(c.recheio_principal_nome, c.camadas_principal, c.recheio_secundario_nome, c.camadas_secundario)}
                    </td>
                    <td className="border border-gray-300 px-2 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => abrirEditar(c)}
                          className="bg-gray-500 text-white p-1.5 rounded hover:bg-gray-600 transition-colors" title="Editar">✏️</button>
                        <button onClick={() => desativar(c.id)}
                          className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors" title="Remover">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Rodapé */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700 text-sm">
                Total: {combinacoesFiltradas.length} {combinacoesFiltradas.length === 1 ? 'combinação' : 'combinações'}
              </span>
              <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                {recheios.length} recheios disponíveis
              </span>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">
                {editando ? 'Editar Combinação' : 'Nova Combinação'}
              </h2>
              <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600 text-xl font-bold">
                ×
              </button>
            </div>

            <form onSubmit={salvar} className="px-6 py-5 space-y-4">

              {/* Recheios */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recheio principal *
                </label>
                <select
                  name="recheio_principal_id"
                  value={form.recheio_principal_id}
                  onChange={handleRecheioChange}
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                >
                  <option value="">Selecione...</option>
                  {recheios.map(r => (
                    <option key={r.id} value={r.id}>{r.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recheio secundário *
                </label>
                <select
                  name="recheio_secundario_id"
                  value={form.recheio_secundario_id}
                  onChange={handleRecheioChange}
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                >
                  <option value="">Selecione...</option>
                  {recheios.map(r => (
                    <option key={r.id} value={r.id}>{r.nome}</option>
                  ))}
                </select>
              </div>

              {/* Distribuição */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Distribuição das 3 camadas
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Camadas do principal</p>
                    <select
                      name="camadas_principal"
                      value={form.camadas_principal}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </div>
                  <div className="text-gray-400 text-sm mt-4">+</div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Camadas do secundário</p>
                    <div className="w-full border border-gray-100 bg-white rounded-lg px-3 py-2 text-sm text-gray-500">
                      {form.camadas_secundario} {Number(form.camadas_secundario) === 1 ? 'camada' : 'camadas'}
                    </div>
                  </div>
                  <div className="text-gray-400 text-sm mt-4">= 3</div>
                </div>

                {/* Preview visual */}
                {form.recheio_principal_id && form.recheio_secundario_id && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-400 mb-2">Preview:</p>
                    {renderCamadas(
                      recheios.find(r => r.id === Number(form.recheio_principal_id))?.nome || '',
                      Number(form.camadas_principal),
                      recheios.find(r => r.id === Number(form.recheio_secundario_id))?.nome || '',
                      Number(form.camadas_secundario),
                    )}
                  </div>
                )}
              </div>

              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da combinação
                </label>
                <input
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  placeholder="Gerado automaticamente"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Deixe em branco para gerar automaticamente pelo nome dos recheios.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  className="bg-pink-600 hover:bg-pink-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}